import { useSnackbar } from '@automata/ui';
import { logger } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import {
  GetSchedulerVersionsError,
  GetScriptsInfoError,
  useGetScriptsInfo,
} from '@automata/wfb-api';
import { WorkflowConfig } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { useEffect, useState } from 'react';

import { addMissingLayout } from './addMissingLayout';
import { selectDriversByNameAndVersion } from '../../features/workflow/selectors';
import { setSchedulerVersion } from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { loadExistingWorkflow, transformLoadedWorkflow } from '../../migration';
import {
  SchedulerVersionResolutionPrompt,
  useInitializeSchedulerVersion,
} from '../useInitializeSchedulerVersion';
import { getWorkflowInstrumentsForSdk } from '../useInitializeWorkcellAndDrivers/utils';

export const getLayoutData = (
  workflow: WorkflowConfig
): Record<string, any> | null => {
  const rawLayoutData = workflow.workflow.layout?.data;
  if (typeof rawLayoutData === 'object') return rawLayoutData;

  if (typeof rawLayoutData === 'string') {
    try {
      const parsedLayoutData: unknown = JSON.parse(rawLayoutData);
      if (typeof parsedLayoutData === 'object') return parsedLayoutData;
    } catch (error) {
      logger.error('Failed to parse layout data', error);
    }
  }

  return null;
};

export const useInitializeWorkflow = (
  workflowData: WorkflowConfig | undefined,
  isFetchingWorkflow: boolean
): {
  schedulerVersionResolutionPrompt: SchedulerVersionResolutionPrompt | null;
  isLoading: boolean;
  error: GetSchedulerVersionsError | GetScriptsInfoError | Error | null;
} => {
  const dispatch = useAppDispatch();
  const sdkInteropFlagEnabled = useFlag('fe.canvas_sdk_interoperability');
  const { enqueueSnackbar } = useSnackbar();

  const driversByNameAndVersion = useAppSelector(selectDriversByNameAndVersion);

  const workflowMaestroVersion = workflowData?.scheduler_config?.version;

  const [maestroVersion, setMaestroVersion] = useState<string | undefined>(
    undefined
  );

  const {
    schedulerVersionResolutionPrompt,
    isLoading: isLoadingSchedulerVersions,
    error: schedulerVersionError,
  } = useInitializeSchedulerVersion(
    isFetchingWorkflow,
    workflowMaestroVersion,
    setMaestroVersion
  );

  // we can get scripts from redux, but this way we can be sure that load has
  // completed so we don't get false positives that the eval scripts are unsupported
  const {
    data: getScriptsData,
    error: getScriptsError,
    isLoading: isLoadingScripts,
  } = useGetScriptsInfo({}, { enabled: sdkInteropFlagEnabled });

  useEffect(() => {
    if (getScriptsError) {
      logger.error('Error fetching scripts info', getScriptsError);
      enqueueSnackbar('Failed to fetch script information', {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, getScriptsError]);

  // Handle new workflows (when workflowData is undefined) - set maestro version
  // Only do this if we're not currently fetching a workflow
  useEffect(() => {
    if (!workflowData && !isFetchingWorkflow && maestroVersion !== undefined) {
      dispatch(setSchedulerVersion(maestroVersion));
    }
  }, [workflowData, isFetchingWorkflow, maestroVersion, dispatch]);

  useEffect(() => {
    if (workflowData && maestroVersion !== undefined) {
      const workcellInstruments = getWorkflowInstrumentsForSdk(workflowData);

      // todo: do we need to add other checks here?
      const layoutData = getLayoutData(workflowData);
      const isMissingLayoutData =
        layoutData === null || Object.keys(layoutData).length === 0;

      // if interop is enabled and script functions aren't available yet then wait.
      // Otherwise we can sometimes hit a race condition where missing layout is not added and page crashes.
      if (!getScriptsData?.functions && sdkInteropFlagEnabled) return;

      let workflowToTransform = workflowData;

      workflowToTransform = {
        ...workflowToTransform,
        scheduler_config: {
          scheduler: 'maestro',
          version: maestroVersion,
        },
      };

      if (
        sdkInteropFlagEnabled &&
        isMissingLayoutData &&
        getScriptsData?.functions
      ) {
        workflowToTransform = addMissingLayout(
          workflowToTransform,
          workcellInstruments,
          getScriptsData.functions,
          driversByNameAndVersion
        );
      }

      const transformedWorkflow = transformLoadedWorkflow(workflowToTransform);
      dispatch(loadExistingWorkflow(transformedWorkflow));
    }
  }, [
    workflowData,
    dispatch,
    sdkInteropFlagEnabled,
    getScriptsData,
    driversByNameAndVersion,
    maestroVersion,
  ]);

  return {
    schedulerVersionResolutionPrompt,
    isLoading: isLoadingScripts || isLoadingSchedulerVersions,
    error: schedulerVersionError || getScriptsError,
  };
};
